import { useMutation, useQueryClient } from 'react-query';
import { toast } from 'sonner';

import { APIBaseChronos } from '@/api/hosts';
import { QueryEntity } from '@/api/types';
import useGetFetchConfig, { FetchMethod } from '@/api/useGetFetchConfig';

/**
 * Synchronously deletes a single unprocessed document. This hits the lightweight delete endpoint
 * that simply removes the doc/file from the DB (and its raw S3 objects), bypassing the ML-pipeline
 * deletion flow used for processed documents.
 */
export const useDeleteUnprocessedDoc = (matterId: string, onDeleted?: () => void) => {
  const { getFetchConfig } = useGetFetchConfig();
  const queryClient = useQueryClient();

  const deleteDoc = async (docId: string): Promise<void> => {
    const fetchConfig = getFetchConfig({ method: FetchMethod.DELETE });
    const response = await fetch(`${APIBaseChronos}/api/matter/${matterId}/docs/${docId}/unprocessed`, fetchConfig);
    if (!response.ok) {
      const body = (await response.json().catch(() => null)) as { message?: string } | null;
      throw new Error(body?.message ?? 'Deleting document failed');
    }
  };

  return useMutation({
    mutationFn: (docId: string) => deleteDoc(docId),
    onSuccess: () => {
      toast.success('Document deleted');
      queryClient.invalidateQueries({ queryKey: [QueryEntity.SubsetDocs] });
      queryClient.invalidateQueries({ queryKey: [QueryEntity.SubsetDocsCount] });
      queryClient.invalidateQueries({ queryKey: [QueryEntity.FilteredDocIds] });
      queryClient.invalidateQueries({ queryKey: [QueryEntity.DocIds] });
      queryClient.invalidateQueries({ queryKey: [QueryEntity.DocTotals] });
      onDeleted?.();
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to delete document. Please try again.');
    },
  });
};
